/* ══════════════════════════════════════════════════════════════════════════
 * ZARYNA OUT LOUD — THE TWO STAGES, IN THE BROWSER
 *
 * Al, 23 September 2026: *"How does Google do it?… can it have a
 * conversation? Hi Bill, how are you?"*
 *
 * ── STAGE 1 · THE MICROPHONE. FREE. ──────────────────────────────────────
 *
 * This IS how Google does it: the phone transcribes, not the server. You tap
 * the microphone, you speak, your words appear in the box, and she answers
 * the way she already does. `SpeechRecognition` is the browser's own engine —
 * the one behind the keyboard's dictation key — and it never touches Eleat's
 * Gemini account. Nothing about Stage 1 costs a penny.
 *
 * ── STAGE 2 · THE CONVERSATION. METERED, AND OFF UNTIL AL SAYS OTHERWISE. ─
 *
 * She greets, they talk back and forth, she can be interrupted mid-sentence.
 * This is Gemini Live over a WebSocket the phone opens directly to Google.
 *
 * ⚠️ AND IT NEVER HOLDS ELEAT'S KEY. The server mints a one-use ephemeral
 * token — good for a single session, a few minutes, locked to one model — and
 * that is all that ever reaches this file. See `zaryna.voice.js`.
 *
 * ⚠️ EVERY PROTOCOL DETAIL BELOW WAS READ OFF GOOGLE'S DOCUMENTATION ON
 * 23 SEPTEMBER 2026, NOT REMEMBERED (CLAUDE.md RULE ZERO):
 *   https://ai.google.dev/gemini-api/docs/live-api/get-started-websocket
 *   https://ai.google.dev/gemini-api/docs/live-api/ephemeral-tokens
 * The endpoint for a token-authenticated session is
 * `…BidiGenerateContentConstrained?access_token=…` — NOT the key-authenticated
 * `BidiGenerateContent`. Audio in is 16-bit PCM at 16kHz; audio out is 24kHz.
 * These are facts with a shelf life: check the pages before changing one.
 * ══════════════════════════════════════════════════════════════════════════ */

import { call, API_BASE, getToken } from './api.js';

/* ══════════════════════════════════════════════════════════════════════════
 * STAGE 1 — THE MICROPHONE
 * ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE ELEATER WAS SHOWN THE BROWSER'S ERROR CODE.
 *
 * Al, 24 September 2026, from his phone: the screen read
 * **"The microphone stopped: network"** in red, and tapping it again "said
 * nothing and it didn't answer."
 *
 * `network` is a raw `SpeechRecognitionErrorEvent.error` value, printed
 * straight through. It tells somebody nothing and it tells them nothing in a
 * word that sounds like their phone is broken.
 *
 * ⚠️ AND `network` HAS A REAL, SPECIFIC CAUSE WORTH SAYING OUT LOUD. Speech
 * recognition in the browser is NOT on the phone — Chrome streams the audio to
 * Google's speech service and the transcript comes back over the network.
 * There is no offline fallback. So `network` means that round trip failed:
 * usually an in-app browser (a link opened inside WhatsApp, Instagram or
 * Gmail) or a non-Google Chromium build, which expose the API and then refuse
 * every request — or a route that cannot reach Google at all.
 *
 * The one thing an Eleater can actually do about it is open Eleat in their
 * own browser, or type instead. So that is what it says.
 * ══════════════════════════════════════════════════════════════════════════ */
const MESSAGES = {
  'not-allowed': 'Eleat needs permission to use the microphone.',
  'service-not-allowed': 'Eleat needs permission to use the microphone.',
  'audio-capture': 'Eleat could not reach the microphone on this phone.',
  network: 'Speaking needs a connection this browser cannot make. Open Eleat in Chrome or Safari, or type your question.',
  'language-not-supported': 'Speaking is not available in this language yet. Type your question and she will answer in it.',
  unknown: 'The microphone stopped. Type your question instead.',
};

/* ⚠️ THE PREFIXED NAME IS NOT OPTIONAL. Safari and every iPhone browser
 * expose this only as `webkitSpeechRecognition`; Hong Kong is an iPhone city,
 * so checking the unprefixed name alone would have hidden the microphone from
 * most of the launch market. */
function SR() {
  return typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE MICROPHONE, HEARD BY ELEAT ITSELF — 4 Oct 2026
 * Al: "Zaryna is within our own system… never go into Google." The browser's
 * own recogniser (below) sends the voice to Google in Chrome and Apple in
 * Safari. When the Zaryna server's speech-to-text is set up (GET /zaryna/voice
 * says transcribe.enabled), the question is recorded here and sent to Eleat's
 * own Whisper instead (POST /zaryna/voice/transcribe) — Cantonese, Mandarin,
 * English and the rest. It stops when tapped, after a 4-second pause, or at a minute.
 * ══════════════════════════════════════════════════════════════════════════ */
export function recordAvailable() {
  return typeof window !== 'undefined' && !!(navigator.mediaDevices
    && navigator.mediaDevices.getUserMedia && (window.AudioContext || window.webkitAudioContext));
}

/* the question as a 16 kHz mono WAV — the one format every hearer takes
   (Gemini documents wav/mp3; a phone's own recorder makes webm or mp4) */
function wavOf(chunks, rate) {
  const len = chunks.reduce((n, c) => n + c.length, 0);
  const all = new Float32Array(len);
  let o = 0; for (const c of chunks) { all.set(c, o); o += c.length; }
  const OUT = 16000; const step = rate / OUT;
  const count = Math.floor(len / step);
  const buf = new ArrayBuffer(44 + count * 2); const v = new DataView(buf);
  const str = (at, t) => { for (let i = 0; i < t.length; i += 1) v.setUint8(at + i, t.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + count * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, OUT, true); v.setUint32(28, OUT * 2, true); v.setUint16(32, 2, true);
  v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, count * 2, true);
  for (let i = 0; i < count; i += 1) {
    const x = Math.max(-1, Math.min(1, all[Math.floor(i * step)] || 0));
    v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/* ⚠️⚠️ ANY LANGUAGE, WHATEVER THE SETTING — Al-Noor, 9 Oct 2026. A tester with
   English in Settings spoke Cantonese and nothing was understood: the phone's
   recogniser listens for one language. Here the question is recorded and the
   language is worked out from the sound on Eleat's side. `lang` is sent only
   as a note; nothing forces it. */
/* ⚠️ SAYS WHAT IT IS DOING — Al-Noor, 11 Oct 2026: "when the mic is pressed
   you don't know when to speak and how long to wait for the answer."
   onReady: the microphone is open (speak now) · onLevel: how loud, 0–1, for
   the moving bar · onSending: they stopped, Zaryna is writing it down. */
export function listenOnEleat({ lang = 'en', onDone, onError, onQuiet, onReady, onLevel, onSending } = {}) {
  let stream = null; let node = null; let src = null; let timer = null; let done = false;
  const chunks = []; let peakAll = 0;
  /* made inside the tap, so iOS lets it make sound-free capture */
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const send = async () => {
    try { node && node.disconnect(); src && src.disconnect(); } catch (_e) { /* gone */ }
    try { stream && stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* gone */ }
    const rate = ctx.sampleRate;
    ctx.close().catch(() => {});
    const total = chunks.reduce((n, c) => n + c.length, 0);
    if (total < rate * 0.4 || peakAll < 0.02) { onQuiet && onQuiet(); return; }
    onSending && onSending();
    try {
      const token = getToken();
      const wav = wavOf(chunks, rate);
      /* ⚠️ ONE MORE TRY WHEN THE CONNECTION DROPS — 20 Oct 2026: a friend in
         Calgary spoke Portuguese and got the browser's own "NetworkError when
         attempting to fetch resource." The same Portuguese question sent to
         the live server twice was heard word for word in ~7 s, so the line
         dropped on the way. A dropped upload is tried once more, and if it
         drops again the message is api.js's own, never the browser's. */
      const post = () => fetch(`${API_BASE}/zaryna/voice/transcribe?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'audio/wav', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: wav,
      });
      let r;
      try { r = await post(); }
      catch (_first) {
        try { r = await post(); }
        catch (_second) { throw new Error('Could not reach Eleat. Check your connection and try again.'); }
      }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Zaryna could not hear that just now.');
      if (!d.text) { onQuiet && onQuiet(); return; }
      onDone && onDone(d.text);
    } catch (e) { onError && onError(e); }
  };
  const finish = () => {
    if (done) return; done = true;
    clearTimeout(timer);
    send();
  };
  ctx.resume && ctx.resume().catch(() => {});
  navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
    if (done) { s.getTracks().forEach((t) => t.stop()); return; }
    stream = s;
    src = ctx.createMediaStreamSource(s);
    node = ctx.createScriptProcessor(4096, 1, 1);
    /* a pause of 4 s after speaking ends the question; a tap ends it at once;
       a minute is the limit.
       ⚠️ THE LINE BETWEEN VOICE AND ROOM IS MEASURED, NOT FIXED — Al-Noor,
       11 Oct 2026: "when you press it, it becomes darker… but nothing
       happens." A fixed 0.06 was louder than a quiet voice on a phone that
       lowers its own gain, so speech was never noticed, the pause never
       ended it, and it sat recording to the 15-second cut-off. The first
       half-second is the room; speech is anything clearly above it. */
    let spoke = false; let quietSince = Date.now(); const t0 = Date.now(); let floor = 0; let frames = 0; let voiceTop = 0;
    node.onaudioprocess = (e) => {
      if (done) return;
      const d = e.inputBuffer.getChannelData(0);
      chunks.push(new Float32Array(d));
      let peak = 0; for (let i = 0; i < d.length; i += 1) peak = Math.max(peak, Math.abs(d[i]));
      peakAll = Math.max(peakAll, peak);
      frames += 1;
      if (Date.now() - t0 < 500) { floor = Math.max(floor, peak); }
      const line = Math.max(0.025, Math.min(0.2, floor * 2.2));
      onLevel && onLevel(Math.min(1, (peak / Math.max(line * 3, 0.08))));
      /* ⚠️⚠️ THEIR VOICE, NOT THE ROOM — Al-Noor, 31 Oct 2026: "Ppl don't know
         to click to talk and click to stop so recording goes on and she picks
         up the conversation of other[s]." In a room of people talking it was
         never quiet, so it ran to the minute and wrote down the next table.
         Once they have spoken, only sound near THEIR loudness keeps it going
         (the person holding the phone is far louder than voices across the
         room); anything under a third of it counts as their pause. */
      if (peak > line) voiceTop = Math.max(voiceTop, peak);
      const theirs = Math.max(line, voiceTop * 0.33);
      if (peak > theirs) { spoke = true; quietSince = Date.now(); }
      /* ⚠️ 2.5 s — Al-Noor, 31 Oct 2026. 4 s let other people's talk run on;
         1.5 s cut him off mid-sentence (as 1.3 s did on 13 Oct); 2.5 s is the
         pause that held. The voice filter above still ignores the room. The
         restaurant side and Command use the same numbers
         (tools/shared/eleat_listen.js). */
      else if (spoke && Date.now() - quietSince > 2500) finish();
      /* nothing said for 7 seconds: stop and say so, rather than wait */
      else if (!spoke && Date.now() - t0 > 7000) finish();
    };
    src.connect(node); node.connect(ctx.destination);
    /* ⚠️ A PHONE THAT HAS NOT STARTED THE SOUND YET (iOS keeps it asleep until
       asked twice) — asked again, so the bar moves and the pause is heard */
    setTimeout(() => { if (!done && frames === 0 && ctx.resume) ctx.resume().catch(() => {}); }, 400);
    onReady && onReady();
    /* ⚠️ 20 SECONDS AT MOST — Al-Noor, 31 Oct 2026 (was a minute, which is
       how a forgotten tap recorded a whole conversation). Long enough for a
       three-part question; 12 s once cut people off mid-sentence. */
    timer = setTimeout(finish, 20000);
  }).catch(() => {
    done = true; ctx.close().catch(() => {});
    onError && onError(new Error(MESSAGES['not-allowed'] || 'Eleat needs your permission to use the microphone.'));
  });
  return finish;
}

/* ══════════════════════════════════════════════════════════════════════════
 * LIVE LISTENING — THEIR WORDS AS THEY SPEAK, ENDED ON A FINISHED SENTENCE
 * (Al-Noor yes, 31 Oct 2026: "ChatGPT does the same thing… Google does that
 * with Gemini"). The microphone streams to a listen-only Gemini Live session
 * (POST /zaryna/voice/hear-live → a one-use token); Google writes the words
 * down as they come and they appear in the box. The question ends when the
 * words are a FINISHED sentence and they pause (0.9 s), or after 3 s of quiet
 * on an unfinished one — "So, I've been told Hong Kong is…" + a pause keeps
 * listening. A tap ends it at once; 20 s at most.
 * ⚠️ NOTHING IS LOST IF GOOGLE CANNOT BE REACHED: the same audio is recorded,
 * and without live words it is sent to /voice/transcribe as before.
 * (scripts/hear-live-check.js keeps the same rule — keep them in step.)
 * ══════════════════════════════════════════════════════════════════════════ */
const JOINERS = /\b(and|or|but|so|with|the|a|an|to|of|for|in|at|on|is|are|was|because|like|um|uh|er|my|your|our|some|any|which|that|if|then)\s*[,.]?\s*$/i;
export function finishedSentence(text) {
  const t = String(text || '').trim();
  return /[.?!。？！؟]["'”’)]*$/.test(t) && !JOINERS.test(t.replace(/[.?!。？！؟]+$/, ''));
}
export function endOfTurn({ text, quietMs }) {
  if (!text) return quietMs >= 2500;          // no live words: the recorded question's own pause
  if (finishedSentence(text) && quietMs >= 900) return true;
  return quietMs >= 3000;
}

export function hearLive({ lang = 'en', onDone, onError, onQuiet, onReady, onLevel, onSending, onPartial } = {}) {
  let stream = null; let node = null; let src = null; let timer = null; let done = false;
  let ws = null; let live = false; let text = ''; const before = [];
  const chunks = []; let peakAll = 0;
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const rate = ctx.sampleRate;
  const step = Math.max(1, Math.round(rate / IN_RATE));
  const closeSocket = () => { try { ws && ws.close(); } catch (_e) { /* closed */ } ws = null; };
  const release = () => {
    try { node && node.disconnect(); src && src.disconnect(); } catch (_e) { /* gone */ }
    try { stream && stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* gone */ }
    ctx.close().catch(() => {});
  };
  /* the recorded question — the old way, when no live words came */
  const sendRecorded = async () => {
    const total = chunks.reduce((n, c) => n + c.length, 0);
    if (total < rate * 0.4 || peakAll < 0.02) { onQuiet && onQuiet(); return; }
    onSending && onSending();
    try {
      const token = getToken();
      const post = () => fetch(`${API_BASE}/zaryna/voice/transcribe?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'audio/wav', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: wavOf(chunks, rate),
      });
      let r;
      try { r = await post(); } catch (_first) {
        try { r = await post(); } catch (_second) { throw new Error('Could not reach Eleat. Check your connection and try again.'); }
      }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Zaryna could not hear that just now.');
      if (!d.text) { onQuiet && onQuiet(); return; }
      onDone && onDone(d.text);
    } catch (e) { onError && onError(e); }
  };
  const finish = () => {
    if (done) return; done = true;
    clearTimeout(timer); release();
    /* ⚠️ CHINESE IS WRITTEN DOWN THE RECORDED WAY (live run, 1 Nov 2026): Google's
       live words turned spoken Cantonese into Mandarin in Simplified characters.
       The live words still show while they speak; the question sent is the
       recorded one, which keeps Cantonese in Traditional. */
    if (live && text.trim() && !/[\u3400-\u9fff]/.test(text)) {
      /* a moment for Google's last words, then the question is theirs */
      setTimeout(() => { closeSocket(); onDone && onDone(text.trim()); }, 600);
      return;
    }
    closeSocket();
    sendRecorded();
  };
  /* the one-use token, then the listen-only session (any failure = the recorded way) */
  call('/zaryna/voice/hear-live', { method: 'POST' }).then((t) => {
    if (done || !t || !t.token || typeof WebSocket !== 'function') return;
    const url = `${String(t.base_url).replace(/^http/, 'ws').replace(/\/v1beta$/, '')}/ws/google.ai.generativelanguage.v1beta`
      + `.GenerativeService.BidiGenerateContentConstrained?access_token=${encodeURIComponent(t.token)}`;
    ws = new WebSocket(url);
    ws.onopen = () => { try { ws.send(JSON.stringify({ setup: { model: `models/${t.model}`,
      generationConfig: { responseModalities: ['AUDIO'], maxOutputTokens: 1 }, inputAudioTranscription: {} } })); } catch (_e) { /* failed */ } };
    /* ⚠️ Google's own end-of-speech, as the server's hearSetup (live run, 1 Nov
       2026): marking start and end ourselves, the words came only after the end */
    ws.onerror = () => { live = false; };
    ws.onclose = () => { live = false; };
    ws.onmessage = async (ev) => {
      let m; try { m = JSON.parse(ev.data instanceof Blob ? await ev.data.text() : ev.data); } catch (_e) { return; }
      if (m.setupComplete) {
        live = true;
        while (before.length) ws.send(before.shift());
      }
      const it = m.serverContent && m.serverContent.inputTranscription;
      if (it && it.text && !done) { text += it.text; onPartial && onPartial(text.trim()); }
    };
  }).catch(() => { /* the recorded way */ });
  ctx.resume && ctx.resume().catch(() => {});
  navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
    if (done) { s.getTracks().forEach((t) => t.stop()); return; }
    stream = s;
    src = ctx.createMediaStreamSource(s);
    node = ctx.createScriptProcessor(4096, 1, 1);
    let spoke = false; let quietSince = Date.now(); const t0 = Date.now(); let floor = 0; let frames = 0; let voiceTop = 0;
    node.onaudioprocess = (e) => {
      if (done) return;
      const d = e.inputBuffer.getChannelData(0);
      chunks.push(new Float32Array(d));
      /* to Google at 16 kHz, averaged down from the phone's own rate */
      const small = new Float32Array(Math.floor(d.length / step));
      for (let i = 0; i < small.length; i += 1) { let sum = 0; for (let k = 0; k < step; k += 1) sum += d[i * step + k]; small[i] = sum / step; }
      const msg = JSON.stringify({ realtimeInput: { audio: { data: bytesToB64(floatToPcm16(small)), mimeType: `audio/pcm;rate=${Math.round(rate / step)}` } } });
      if (live && ws && ws.readyState === 1) ws.send(msg); else if (before.length < 80) before.push(msg);
      let peak = 0; for (let i = 0; i < d.length; i += 1) peak = Math.max(peak, Math.abs(d[i]));
      peakAll = Math.max(peakAll, peak);
      frames += 1;
      if (Date.now() - t0 < 500) { floor = Math.max(floor, peak); }
      const line = Math.max(0.025, Math.min(0.2, floor * 2.2));
      onLevel && onLevel(Math.min(1, (peak / Math.max(line * 3, 0.08))));
      /* their voice, not the room (31 Oct 2026, as listenOnEleat) */
      if (peak > line) voiceTop = Math.max(voiceTop, peak);
      if (peak > Math.max(line, voiceTop * 0.33)) { spoke = true; quietSince = Date.now(); }
      else if (spoke && endOfTurn({ text: live ? text : '', quietMs: Date.now() - quietSince })) finish();
      else if (!spoke && Date.now() - t0 > 7000) finish();
    };
    src.connect(node); node.connect(ctx.destination);
    setTimeout(() => { if (!done && frames === 0 && ctx.resume) ctx.resume().catch(() => {}); }, 400);
    onReady && onReady();
    timer = setTimeout(finish, 20000);
  }).catch(() => {
    done = true; closeSocket(); ctx.close().catch(() => {});
    onError && onError(new Error(MESSAGES['not-allowed'] || 'Eleat needs your permission to use the microphone.'));
  });
  return finish;
}

/** Can this phone do it at all? The screen asks before it shows a button. */
export function micAvailable() {
  return !!SR();
}

/**
 * Listen once, hand back what was said.
 *
 * ⚠️ `onPartial` EXISTS BECAUSE SILENCE READS AS BROKEN. Speech recognition
 * takes a beat, and a button that does nothing visible for two seconds is a
 * button people press again. The interim words go straight into the box as
 * they arrive, so the screen is visibly listening.
 *
 * Returns a `stop()` — the same button must be able to call it off.
 */
export function listen({ lang = 'en-US', onPartial, onDone, onError, onQuiet } = {}) {
  const Rec = SR();
  if (!Rec) { onError && onError(new Error('This phone cannot listen.')); return () => {}; }

  const rec = new Rec();
  rec.lang = lang;
  rec.interimResults = true;
  /* ⚠️ NOT CONTINUOUS. One question, then she answers — the search-bar
   * microphone, not an open line. An open line here would be Stage 2 with
   * none of Stage 2's controls. */
  rec.continuous = false;
  rec.maxAlternatives = 1;

  let finalText = '';
  let stopped = false;   /* the Eleater tapped the microphone to finish */
  let failed = false;    /* the browser raised an error — nothing to send */

  rec.onresult = (e) => {
    let interim = '';
    for (let i = e.resultIndex; i < e.results.length; i += 1) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interim += r[0].transcript;
    }
    onPartial && onPartial((finalText + interim).trim());
  };

  rec.onerror = (e) => {
    /* ⚠️⚠️ `failed = true` USED TO SIT ON THIS LINE, ABOVE BOTH ESCAPES — and
     * that put back the very bug the comment below says it removed. The
     * `aborted` escape stopped the MESSAGE and not the FLAG, so a browser that
     * reports the Eleater's own stop as `aborted` set `failed`, `onend` skipped
     * `onDone`, and the question was thrown away in silence. Found by reading
     * the file after it was written, not by anything that ran. */
    /* ⚠️ 'no-speech' IS NOT A FAULT — somebody changing their mind is not an
     * error, and a red message for it would be Eleat telling them off for
     * tapping a button. It still has to SAY something, though: see below. */
    if (e.error === 'no-speech') { onQuiet && onQuiet(); return; }
    /* ⚠️ 'aborted' IS THE ELEATER'S OWN STOP and is handled by stop(), which
     * sends the words rather than throwing them away. Reaching here means the
     * browser aborted by itself, which is worth saying. */
    if (e.error === 'aborted' && stopped) return;
    /* only a real failure gets here, and only a real failure suppresses the
       words — everything above this line still has something worth sending */
    failed = true;
    onError && onError(new Error(MESSAGES[e.error] || MESSAGES.unknown));
  };

  /* ⚠️ STOPPING MUST NOT THROW THE WORDS AWAY. This used to set a flag that
   * made `onend` skip `onDone` entirely — so tapping the microphone to finish
   * speaking, which is the obvious way to finish speaking, discarded the whole
   * question and asked her nothing. Only a recognition that ran out on its own
   * ever reached her. */
  rec.onend = () => { if (!failed) onDone && onDone(finalText.trim()); };

  try { rec.start(); } catch (e) { failed = true; onError && onError(e); }

  return function stop() { stopped = true; try { rec.stop(); } catch (_e) { /* already gone */ } };
}


/* ⚠️ THE LANGUAGE TAG SPEECH RECOGNITION WANTS IS NOT THE ONE ELEAT STORES.
 * `locales` holds zh-HK, zh-TW, zh-CN. The recogniser wants a full BCP-47 tag
 * with a region, and Cantonese is `yue-Hant-HK` rather than `zh-HK` — pass
 * zh-HK and an iPhone listens for Mandarin while somebody speaks Cantonese.
 * The launch three are Al's: English, Cantonese, Mandarin. */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE MAP HELD FOUR ENTRIES AND EVERYTHING ELSE BECAME AMERICAN ENGLISH.
 *
 * Al, 24 September 2026: *"if someone talks to it in Cantonese, Mandarin or
 * the approved languages, will it answer back in the language that they are
 * speaking, in their own language script? If it's Mandarin, it goes into
 * Mandarin. If it's Arabic, it's in Arabic. Portuguese, it's in Portuguese."*
 *
 * TYPING already works — her persona says *"You are fully MULTILINGUAL —
 * always reply in the user's language"*, so Arabic in, Arabic out. **Speaking
 * did not.** `map[…] || 'en-US'` meant an Arabic speaker's phone listened for
 * American English and transcribed nonsense, silently. Nothing threw. Nobody
 * could have seen it except by speaking Arabic at it.
 *
 * ⚠️ THE FALLBACK IS THE FAULT, NOT THE LENGTH OF THE LIST. Handing the
 * recogniser the Eleater's OWN tag is right even for a language nobody
 * listed — the browser either supports it or raises `language-not-supported`,
 * which now says so in words. Guessing English can only ever be wrong.
 *
 * The named entries below exist because three of them are NOT what you would
 * guess: Cantonese is `yue-Hant-HK`, not `zh-HK`, and the two Mandarins are
 * `cmn-`. Pass `zh-HK` and an iPhone listens for Mandarin while somebody
 * speaks Cantonese. Everything else is a plain BCP-47 tag and needs no map.
 * ══════════════════════════════════════════════════════════════════════════ */
const SPEECH_TAGS = {
  'en': 'en-US',
  'zh-HK': 'yue-Hant-HK',     // Cantonese, Traditional — Hong Kong
  'zh-Hant': 'yue-Hant-HK',   // what Eleat stores for Cantonese · HK
  'zh-TW': 'cmn-Hant-TW',     // Mandarin, Traditional — Taiwan
  'zh-CN': 'cmn-Hans-CN',     // Mandarin, Simplified — Mainland
  'zh-Hans': 'cmn-Hans-CN',   // what Eleat stores for Mandarin
  'zh': 'cmn-Hans-CN',
  /* ⚠️ HOW PHONES WRITE THEM — audit, 7 Oct 2026: a Hong Kong phone sends
     zh-Hant-HK and a Macau phone zh-MO, and both fell through to `zh` →
     Mandarin Simplified, so Cantonese speech was heard as Mandarin. */
  'zh-Hant-HK': 'yue-Hant-HK', 'zh-MO': 'yue-Hant-HK', 'zh-Hant-MO': 'yue-Hant-HK',
  'yue': 'yue-Hant-HK', 'yue-HK': 'yue-Hant-HK', 'yue-Hant-HK': 'yue-Hant-HK',
  'zh-Hant-TW': 'cmn-Hant-TW', 'zh-Hans-CN': 'cmn-Hans-CN', 'zh-SG': 'cmn-Hans-CN',
};

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ WHICH LANGUAGE IS "THEIRS" — AND IT WAS NEVER THE ONE THEY CHOSE.
 *
 * The microphone read `navigator.language`, which is the PHONE's language.
 * Somebody who set Eleat to 繁體中文 on an English handset was dictating into
 * an English recogniser, with their own choice sitting unused one screen away
 * — `users.preferred_language`, a real column since migration 060.
 *
 * Their Eleat choice first, the phone second. One key, named once, because
 * two copies of a storage key is the same fault as two copies of a card.
 * ══════════════════════════════════════════════════════════════════════════ */
export const LANG_KEY = 'eleat.lang';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ "en" IS NOT A CHOICE. IT IS THE COLUMN'S DEFAULT.
 *
 * Al, 24 September 2026: *"Most of these guys that are coming into the country
 * are using their home phones… most people have their home language."*  He is
 * right, and that is the whole reason this had to change.
 *
 * `users.preferred_language` is `text not null default 'en'` (migration 001).
 * So EVERY Eleater who has never opened the language menu carries 'en' — and
 * Home stored that 'en' on load as though they had picked it. A Frenchman on a
 * French handset who never touched the menu therefore dictated into an ENGLISH
 * recogniser, because a default was being read as a decision.
 *
 * ⚠️ NOTHING WOULD EVER HAVE REPORTED THIS. The transcript comes back as
 * plausible English words; it is simply not what they said.
 *
 * So a language is only "theirs" if they PICKED it. `rememberLanguage()` is
 * the only thing that writes the chosen flag, and it is called from the menu
 * and from nowhere else. Absent that, the phone answers — which for the people
 * Al is describing is the right answer anyway.
 * ══════════════════════════════════════════════════════════════════════════ */
const CHOSEN_KEY = 'eleat.lang.chosen';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ WHICH LANGUAGE THE SCREENS ARE IN — Al, 6 Oct 2026: "set English as a
 * default language in North America… and here in Hong Kong… whatever
 * language that they choose in their profile, they would be locked into…
 * if they've signed out and they are coming back in, it's going to be an
 * English default, then they can change it or sign in."
 *   · signed in  → the language on their account (users.preferred_language),
 *                  kept here once read (Home) or chosen (menu, Settings);
 *   · signed out → English, unless they pick one for THIS visit (session);
 *   · signing out forgets the account's language (api.js clearToken).
 * The phone's language no longer chooses the screens. The MICROPHONE still
 * listens in the phone's language when nobody has picked one — a French
 * phone dictates in French (24 Sep) — myLanguage() below.
 * ══════════════════════════════════════════════════════════════════════════ */
const VISIT_KEY = 'eleat.lang.visit';

/** the language menu and Settings call this — the account's language when
    signed in, this visit's when not */
export function rememberLanguage(code) {
  try {
    if (getToken()) {
      localStorage.setItem(LANG_KEY, code);
      localStorage.setItem(CHOSEN_KEY, '1');
    } else {
      sessionStorage.setItem(VISIT_KEY, code);
    }
  } catch { /* a browser that refuses storage still works, it just forgets */ }
}

/** the language read off the account (Home, after signing in) */
export function adoptAccountLanguage(code) {
  try {
    localStorage.setItem(LANG_KEY, code);
    /* 'en' is also the column's default, so it is not marked as picked —
       the microphone keeps listening in the phone's language */
    if (String(code).toLowerCase().startsWith('en')) localStorage.removeItem(CHOSEN_KEY);
    else localStorage.setItem(CHOSEN_KEY, '1');
  } catch { /* forgets */ }
}

/** the language a visitor picked for this visit, or null */
export function visitLanguage() {
  try { return sessionStorage.getItem(VISIT_KEY) || null; } catch { return null; }
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE CITY BEING BROWSED DECIDES WHICH LANGUAGES ARE ON OFFER — migration
 * 160, Al-Noor 8 Oct 2026: "keep out of Canada Chinese and Cantonese…
 * Just English." Each city's list comes off /discovery/markets (`languages`)
 * and Home records which city is being browsed. A language the city does not
 * use is not shown there: the screens are English, and the account's own
 * choice is kept for the next city that does use it.
 * ══════════════════════════════════════════════════════════════════════════ */
const CITY_LANGS_KEY = 'eleat.city.langs';
const BROWSING_KEY = 'eleat.city.browsing';
const langBase = (c) => {
  const s = String(c || '').toLowerCase();
  if (s.startsWith('en')) return 'en';
  if (s === 'zh-hant' || s === 'zh-hk' || s === 'zh-tw') return 'zh-hk';
  if (s === 'zh-hans' || s === 'zh-cn' || s === 'zh') return 'zh-cn';
  if (s.startsWith('fr')) return 'fr-ca';     /* 173 — French Canadian */
  if (s.startsWith('gu')) return 'gu-in';     /* 174 — Gujarati */
  return s;
};

/** Home passes the cities list here once it has it */
export function rememberCityLanguages(markets) {
  try {
    const map = {};
    for (const m of markets || []) if (m && m.code && Array.isArray(m.languages)) map[m.code] = m.languages;
    localStorage.setItem(CITY_LANGS_KEY, JSON.stringify(map));
  } catch { /* forgets */ }
}

/** Home records the city being browsed */
export function rememberBrowsingCity(code) {
  try { if (code) localStorage.setItem(BROWSING_KEY, code); } catch { /* forgets */ }
}

/** the languages of the city being browsed, or null when not known yet */
export function cityLanguages() {
  try {
    const map = JSON.parse(localStorage.getItem(CITY_LANGS_KEY) || 'null');
    const city = localStorage.getItem(BROWSING_KEY);
    return map && city && Array.isArray(map[city]) ? map[city] : null;
  } catch { return null; }
}

/** the screens' language: the account's, else this visit's, else English —
    and English wherever the city being browsed does not use it (160) */
export function screenLanguage() {
  let want = 'en';
  try {
    if (getToken()) want = localStorage.getItem(LANG_KEY) || 'en';
    else want = sessionStorage.getItem(VISIT_KEY) || 'en';
  } catch { return 'en'; }
  const allowed = cityLanguages();
  if (allowed && !allowed.some((l) => langBase(l) === langBase(want))) return 'en';
  return want;
}

/** the microphone's language: one they picked, else the phone's */
export function myLanguage() {
  try {
    if (getToken()) {
      if (localStorage.getItem(CHOSEN_KEY) === '1' && localStorage.getItem(LANG_KEY)) return localStorage.getItem(LANG_KEY);
    } else if (sessionStorage.getItem(VISIT_KEY)) {
      return sessionStorage.getItem(VISIT_KEY);
    }
  } catch { /* a browser that refuses storage still has a language */ }
  return (typeof navigator !== 'undefined' && navigator.language) || 'en';
}

export function speechTag(locale) {
  const tag = String(locale || '').trim();
  if (!tag) return 'en-US';
  if (SPEECH_TAGS[tag]) return SPEECH_TAGS[tag];
  const base = tag.split('-')[0];
  /* en-GB, en-CA, en-AU are real recognisers — kept, not turned into en-US */
  if (base !== 'en' && SPEECH_TAGS[base]) return SPEECH_TAGS[base];
  /* ⚠️ THEIR OWN TAG, UNTOUCHED. `pt-BR`, `ar-EG`, `hi-IN`, `ja-JP` are all
     valid and all work; a language the browser cannot do raises
     `language-not-supported`, which is a true answer. `en-US` is a lie. */
  return tag;
}

/* ══════════════════════════════════════════════════════════════════════════
 * STAGE 2 — THE LIVE CONVERSATION
 * ══════════════════════════════════════════════════════════════════════════ */

const IN_RATE = 16000;    // what Google requires we send
const OUT_RATE = 24000;   // what Google sends back

/** Ask Eleat whether either stage is switched on. Never guesses. */
/* ⚠️ THE MICROPHONE WENT MISSING ON THE WAY BACK — Al-Noor, 12 Oct 2026:
 * "when I went back the microphone was missing." Every screen asked this
 * afresh and showed no microphone until the answer came; on a slow connection
 * the answer was late or lost, and nothing asked again. Now the last good
 * answer is kept for the visit (a screen coming back shows the microphone at
 * once) and a lost answer is asked again (3 s, 10 s, 30 s) instead of leaving
 * the microphone off for good. A failure still means OFF, never on. */
/* ⚠️ AND KEPT ON THE PHONE TOO (12 Oct 2026): a page that reloads on a slow
   connection still draws the microphone at once from the last answer this
   phone had; the server's answer then confirms it (or turns it off). */
let lastFlags = (() => { try { return JSON.parse(localStorage.getItem('eleat.voiceFlags') || 'null'); } catch { return null; } })();
const remember = (f) => { lastFlags = f; try { localStorage.setItem('eleat.voiceFlags', JSON.stringify(f)); } catch { /* private mode */ } };
export function knownVoiceFlags() { return lastFlags; }
export async function voiceFlags() {
  for (const wait of [0, 3000, 10000, 30000]) {
    if (wait) await new Promise((r) => setTimeout(r, wait));
    try {
      const f = await call('/zaryna/voice', { auth: true });
      if (f && typeof f === 'object') { remember(f); return f; }
    } catch (_e) { /* asked again below; a wobble keeps voice OFF meanwhile */ }
    if (lastFlags) return lastFlags;
  }
  return lastFlags || { mic: { enabled: false }, live: { enabled: false, max_minutes: 0 } };
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

/* Float samples from the microphone → 16-bit PCM, which is the only shape
 * Google accepts. Clamped, because a sample above 1.0 wraps to a loud click
 * rather than simply being loud. */
function floatToPcm16(input) {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}

/**
 * Open a live conversation.
 *
 * ⚠️ NOTHING HERE DECIDES WHETHER IT IS ALLOWED. The server refuses to mint a
 * token when the switch is off, and that refusal is the control. A check in
 * this file would be a second answer to one question — and the one in the
 * browser is the one anybody can edit.
 *
 * Returns `{ hangUp() }`. Every callback is optional.
 */
export async function startConversation({
  lang = 'en',
  onState,          // 'connecting' | 'listening' | 'speaking' | 'ended'
  onHeard,          // what the Eleater said (transcript), as it arrives
  onSaid,           // what Zaryna said (transcript), as it arrives
  onTurn,           // ⚠️ one COMPLETED exchange — this is what reaches her memory
  onLookup,         // ⚠️ she is asking ELEAT something mid-sentence — see below
  onError,
} = {}) {
  const say = (s) => onState && onState(s);
  say('connecting');

  // 1 · a one-use token from Eleat. The key stays on the server.
  const t = await call('/zaryna/voice/live', { method: 'POST', auth: true });

  // 2 · the socket. Token sessions use the *Constrained* endpoint.
  const wsBase = String(t.base_url).replace(/^http/, 'ws');
  const url = `${wsBase.replace(/\/v1beta$/, '')}/ws/google.ai.generativelanguage.v1beta`
    + `.GenerativeService.BidiGenerateContentConstrained`
    + `?access_token=${encodeURIComponent(t.token)}`;
  const ws = new WebSocket(url);

  let ctx = null; let stream = null; let node = null; let source = null;
  let closed = false;
  let playAt = 0;

  /* ⚠️ EVERY SCHEDULED CHUNK IS HELD, BECAUSE `playAt = 0` STOPS NOTHING.
   * An audit caught this: the interruption handler reset the queue position
   * and the comment claimed the queued audio was dropped. It was not — an
   * AudioBufferSourceNode that has been start()ed plays to the end no matter
   * what any variable says. So she talked straight over somebody cutting in,
   * and resetting playAt to 0 scheduled the NEXT chunk in the past as well.
   * Being able to interrupt her is the difference between a conversation and
   * a recording; it has to actually stop the sound. */
  let playing = [];

  /* ⚠️ THE TRANSCRIPT IS THE ONLY WAY THIS CONVERSATION CAN REACH HER MEMORY.
   * Eleat's server is not in this socket and cannot overhear it. Gemini sends
   * the words in fragments, so they are gathered here and handed over as one
   * exchange when the turn completes. */
  let heard = ''; let said = '';

  function hangUp() {
    if (closed) return;
    closed = true;
    try { ws.close(); } catch (_e) { /* already closed */ }
    try { for (const src of playing) src.stop(); } catch (_e) { /* already finished */ }
    playing = [];
    try { node && node.disconnect(); source && source.disconnect(); } catch (_e) { /* gone */ }
    try { stream && stream.getTracks().forEach((k) => k.stop()); } catch (_e) { /* gone */ }
    try { ctx && ctx.close(); } catch (_e) { /* gone */ }
    say('ended');
  }

  ws.onerror = () => { onError && onError(new Error('The connection to Zaryna dropped.')); hangUp(); };
  ws.onclose = () => hangUp();

  ws.onopen = async () => {
    /* 3 · the setup message MUST be first (Google's docs are explicit).
     *
     * ⚠️ NO systemInstruction IS SENT FROM HERE, AND THAT IS DELIBERATE. Her
     * character is Eleat's, and anything this file sends is editable by
     * anybody with a browser console. The token is locked to the model on the
     * server; her persona belongs on the server too, alongside it. */
    ws.send(JSON.stringify({
      setup: {
        model: `models/${t.model}`,
        responseModalities: ['AUDIO'],
        /* ⚠️ BOTH TRANSCRIPTIONS ON. They are what let a spoken turn be
         * written into the SAME thread as a typed one — one memory of one
         * person, which is the whole point of migration 093. Without them a
         * conversation would be heard and then forgotten. */
        inputAudioTranscription: {},
        outputAudioTranscription: {},
      },
    }));

    // 4 · the microphone
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (_e) {
      onError && onError(new Error('Eleat needs permission to use the microphone.'));
      hangUp(); return;
    }

    ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: IN_RATE });
    source = ctx.createMediaStreamSource(stream);
    /* ⚠️ ScriptProcessorNode IS DEPRECATED AND IS USED ANYWAY, WITH A REASON.
     * The modern replacement (AudioWorklet) needs a second file served from
     * its own URL, which this build does not do. Every current browser still
     * runs this. If the build ever gains worklet support, that is the moment
     * to change it — with a reason, per CLAUDE.md RULE 10. */
    node = ctx.createScriptProcessor(4096, 1, 1);
    source.connect(node);
    node.connect(ctx.destination);

    node.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const pcm = floatToPcm16(e.inputBuffer.getChannelData(0));
      ws.send(JSON.stringify({
        realtimeInput: { audio: { data: bytesToB64(pcm), mimeType: `audio/pcm;rate=${IN_RATE}` } },
      }));
    };

    say('listening');
  };

  ws.onmessage = async (ev) => {
    let msg;
    try {
      const raw = ev.data instanceof Blob ? await ev.data.text() : ev.data;
      msg = JSON.parse(raw);
    } catch (_e) { return; }

    /* ══════════════════════════════════════════════════════════════════
     * ⚠️ SHE IS ASKING ELEAT A QUESTION — THIS IS HOW SHE KNOWS ANYTHING
     *
     * Al, 23 September 2026: *"She has to know all our restaurants… Why would
     * I go outside to Google and go into the World Wide Web?"*
     *
     * A Live session is a socket between this phone and Google; Eleat's
     * server is not in it. So when Zaryna calls `eleat_lookup` mid-sentence,
     * GOOGLE TELLS THIS BROWSER, the browser asks Eleat, and the browser
     * hands the answer back into the conversation. There is no other path,
     * and this handler is it. Without it she is asked where to eat and has
     * nothing to say.
     *
     * ⚠️ AND A FAILURE IS ANSWERED, NEVER SWALLOWED. If Eleat cannot be
     * reached, she is TOLD so and says it out loud. A tool call that never
     * gets a response leaves her waiting mid-sentence, which to the person
     * holding the phone is indistinguishable from being hung up on.
     * ══════════════════════════════════════════════════════════════════ */
    if (msg.toolCall && Array.isArray(msg.toolCall.functionCalls)) {
      const responses = [];
      for (const fc of msg.toolCall.functionCalls) {
        let result;
        try {
          result = onLookup
            ? await onLookup(fc.args || {})
            : { venues: [], note: 'Eleat is not reachable from this screen.' };
        } catch (e) {
          result = { venues: [], note: `Eleat could not answer just now (${e.message}). Say so plainly.` };
        }
        responses.push({ name: fc.name, id: fc.id, response: { result } });
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ toolResponse: { functionResponses: responses } }));
      }
      return;
    }

    const sc = msg.serverContent;
    if (!sc) return;

    /* ⚠️ AN INTERRUPTION MEANS STOP TALKING, NOW. Somebody cutting in while
     * she is mid-sentence is the difference between a conversation and a
     * recording. Google tells us; the queued audio has to be dropped or she
     * carries on over the top of them. */
    if (sc.interrupted) { stopSpeaking(); say('listening'); }

    if (sc.inputTranscription && sc.inputTranscription.text) {
      heard += sc.inputTranscription.text;
      onHeard && onHeard(sc.inputTranscription.text);
    }
    if (sc.outputTranscription && sc.outputTranscription.text) {
      said += sc.outputTranscription.text;
      onSaid && onSaid(sc.outputTranscription.text);
    }

    /* ⚠️ ONE EXCHANGE, HANDED OVER WHEN IT IS FINISHED. Storing fragments
     * would fill her memory with half-sentences; waiting until the end of the
     * conversation would lose everything if somebody simply closed the app.
     * Gemini says when a turn is done, so that is when it is filed — and both
     * halves must be there, or the transcript grows a question with no answer
     * beside it. */
    if (sc.turnComplete) {
      const a = heard.trim(); const b = said.trim();
      heard = ''; said = '';
      if (a && b && onTurn) onTurn({ asked: a, answered: b });
    }

    const parts = (sc.modelTurn && sc.modelTurn.parts) || [];
    for (const p of parts) {
      if (!p.inlineData || !p.inlineData.data) continue;
      say('speaking');
      playPcm(b64ToBytes(p.inlineData.data));
    }
  };

  /* Play a chunk of 24kHz PCM, queued end-to-end.
   *
   * ⚠️ THE QUEUE IS WHAT STOPS HER STUTTERING. Audio arrives in small pieces;
   * playing each one "now" overlaps them into a mess. Each is scheduled where
   * the last one finished. */
  /* Stop her mid-sentence, for real. */
  function stopSpeaking() {
    for (const src of playing) { try { src.stop(); } catch (_e) { /* already finished */ } }
    playing = [];
    playAt = ctx ? ctx.currentTime : 0;   // never 0 — that schedules in the past
  }

  function playPcm(bytes) {
    if (!ctx || closed) return;
    const samples = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
    const buf = ctx.createBuffer(1, samples.length, OUT_RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) ch[i] = samples[i] / 32768;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    playAt = Math.max(playAt, now);
    src.start(playAt);
    playAt += buf.duration;
    playing.push(src);
    src.onended = () => {
      playing = playing.filter((x) => x !== src);
      if (ctx && playAt <= ctx.currentTime + 0.05) say('listening');
    };
  }

  /* ⚠️ A CEILING ONLY IF SUPER ADMIN SET ONE. Migration 092 says why zero
   * means no limit: somebody lonely does not hear "session ended" as a limit,
   * they hear it as being hung up on. Her character is the control. */
  if (t.max_minutes > 0) setTimeout(hangUp, t.max_minutes * 60 * 1000);

  return { hangUp };
}
